import { useState, useEffect } from 'react';
import {
  ChevronRight, RefreshCw, CheckCircle2, AlertCircle, SkipForward, Zap, Server
} from 'lucide-react';
import useSiteSetupStore from '../../../stores/siteSetupStore';
import fetchWithTenant from '../../../utils/fetchWithTenant';

const friendlyName = (type) => type.replace(/_/g, ' ');

const IntegrationConfigStep = ({ onNext }) => {
  const { savedData, markStepComplete } = useSiteSetupStore();
  const selected = savedData?.selectedIntegrations || [];

  const [types, setTypes] = useState([]);
  const [agents, setAgents] = useState([]);
  const [idx, setIdx] = useState(0);
  const [config, setConfig] = useState({});
  const [name, setName] = useState('');
  const [connectionMethod, setConnectionMethod] = useState('DIRECT');
  const [agentId, setAgentId] = useState('');
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Skip entirely if nothing selected
  useEffect(() => {
    if (selected.length === 0) {
      (async () => {
        await markStepComplete('integration_config', { skipped: true });
        onNext();
      })();
    }
    // eslint-disable-next-line
  }, []);

  // Load schema + agents once
  useEffect(() => {
    (async () => {
      try {
        const [typesRes, agentsRes] = await Promise.all([
          fetchWithTenant('/api/integrations/types'),
          fetchWithTenant('/api/md/agents'),
        ]);
        if (typesRes.ok) {
          const d = await typesRes.json();
          setTypes(d.types || d || []);
        }
        if (agentsRes.ok) {
          const d = await agentsRes.json();
          setAgents(d.agents || d || []);
        }
      } catch (err) {
        console.error('[IntegrationConfig] load failed:', err);
        setError(err.message || 'Failed to load integration types');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const currentType = selected[idx];
  const typeDef = types.find(t => t.type === currentType);

  // Reset form when moving to next integration
  useEffect(() => {
    setConfig({});
    setName(friendlyName(currentType || ''));
    setTestResult(null);
    setError(null);
  }, [idx, currentType]);

  const schemaProps = (() => {
    if (!typeDef) return { props: {}, required: [] };
    const schema = typeDef.configSchema || {};
    let props = schema.properties || {};
    if (Object.keys(props).length === 0 && Array.isArray(schema.fields)) {
      props = {};
      for (const f of schema.fields) {
        props[f.key] = {
          title: f.label || f.key,
          type: f.type === 'password' ? 'string' : (f.type || 'string'),
          format: f.type === 'password' ? 'password' : undefined,
          placeholder: f.placeholder || '',
          description: f.helpText || '',
        };
      }
    }
    const required = schema.required
      || (schema.fields || []).filter(f => f.required).map(f => f.key)
      || [];
    return { props, required };
  })();

  const runTest = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await fetchWithTenant('/api/integrations/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: currentType,
          config,
          connectionMethod,
          agentId: connectionMethod === 'AGENT_TUNNEL' ? agentId || null : null,
        }),
      });
      const data = await res.json();
      setTestResult(data);
    } catch (err) {
      setTestResult({ success: false, error: err.message });
    } finally {
      setTesting(false);
    }
  };

  const handleSaveAndContinue = async () => {
    setSaving(true);
    setError(null);
    try {
      const res = await fetchWithTenant('/api/integrations', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: currentType,
          name: name || friendlyName(currentType),
          config,
          connectionMethod,
          agentId: connectionMethod === 'AGENT_TUNNEL' ? agentId || null : null,
          enabled: true,
        }),
      });
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || 'Failed to save integration');
      }
      // Advance to next integration or step
      if (idx + 1 < selected.length) {
        setIdx(idx + 1);
      } else {
        await markStepComplete('integration_config', {
          configuredIntegrations: selected,
        });
        onNext();
      }
    } catch (err) {
      console.error('[IntegrationConfig] save failed:', err);
      setError(err.message || 'Failed to save integration');
    } finally {
      setSaving(false);
    }
  };

  const handleSkipThis = () => {
    if (idx + 1 < selected.length) setIdx(idx + 1);
    else {
      markStepComplete('integration_config', { configuredIntegrations: [], partial: true })
        .then(onNext);
    }
  };

  if (loading) {
    return (
      <div className="site-setup-step">
        <div className="verify-loading">
          <RefreshCw size={20} className="spin" />
          <p>Loading integration options...</p>
        </div>
      </div>
    );
  }

  if (!typeDef) {
    return (
      <div className="site-setup-step">
        <div className="pairing-code-error">
          <AlertCircle size={16} />
          <span>Integration type "{currentType}" not available on this server.</span>
        </div>
        <div className="step-actions">
          <button className="site-setup-btn secondary" onClick={handleSkipThis}>
            Skip this integration <ChevronRight size={16} />
          </button>
        </div>
      </div>
    );
  }

  const onlineAgents = agents.filter(a => a.status === 'ONLINE' || a.status === 'DEGRADED');
  const canSave = !!testResult?.success && !saving;

  return (
    <div className="site-setup-step integration-config-step">
      <div className="integration-substepper">
        <span>Configuring {idx + 1} of {selected.length}:</span>
        <strong>{typeDef.name || friendlyName(currentType)}</strong>
      </div>

      {error && (
        <div className="pairing-code-error">
          <AlertCircle size={16} />
          <span>{error}</span>
        </div>
      )}

      <div className="setup-form-section">
        <div className="setup-form-field full-width">
          <label>Integration name</label>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={typeDef.name}
          />
        </div>

        {Object.entries(schemaProps.props).map(([key, prop]) => {
          const required = schemaProps.required.includes(key);
          const isPassword = prop.format === 'password';
          const isNumber = prop.type === 'number';
          const isBool = prop.type === 'boolean';
          const val = config[key] !== undefined ? config[key] : '';

          return (
            <div key={key} className="setup-form-field full-width">
              <label>
                {prop.title || key}{required ? ' *' : ''}
              </label>
              {prop.description && <p className="setup-hint" style={{ margin: '2px 0 6px' }}>{prop.description}</p>}
              {prop.enum ? (
                <select
                  value={val}
                  onChange={(e) => setConfig({ ...config, [key]: e.target.value })}
                >
                  <option value="">— select —</option>
                  {prop.enum.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              ) : isBool ? (
                <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <input
                    type="checkbox"
                    checked={!!config[key]}
                    onChange={(e) => setConfig({ ...config, [key]: e.target.checked })}
                  />
                  <span style={{ fontSize: 13, color: '#9ca3af' }}>{prop.title || key}</span>
                </label>
              ) : (
                <input
                  type={isPassword ? 'password' : isNumber ? 'number' : 'text'}
                  placeholder={prop.placeholder || ''}
                  value={val}
                  onChange={(e) => setConfig({
                    ...config,
                    [key]: isNumber ? Number(e.target.value) : e.target.value,
                  })}
                />
              )}
            </div>
          );
        })}
      </div>

      <div className="setup-form-section">
        <h4>Connection method</h4>
        <div className="connection-method-pills">
          <button
            type="button"
            className={'method-pill' + (connectionMethod === 'DIRECT' ? ' active' : '')}
            onClick={() => setConnectionMethod('DIRECT')}
          >
            <Zap size={13} /> Direct (VPN / Tailscale)
          </button>
          <button
            type="button"
            className={'method-pill' + (connectionMethod === 'AGENT_TUNNEL' ? ' active' : '')}
            onClick={() => setConnectionMethod('AGENT_TUNNEL')}
          >
            <Server size={13} /> Agent Tunnel
          </button>
        </div>
        {connectionMethod === 'AGENT_TUNNEL' && (
          <div className="setup-form-field full-width" style={{ marginTop: 10 }}>
            <label>Agent</label>
            <select value={agentId} onChange={(e) => setAgentId(e.target.value)}>
              <option value="">— select agent —</option>
              {onlineAgents.map(a => (
                <option key={a.id} value={a.id}>
                  {a.hostname || a.name || a.id} ({a.status})
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="test-connection-panel">
        <div className="test-connection-left">
          {testing ? (
            <>
              <RefreshCw size={18} className="spin" />
              <div>
                <strong>Testing connection...</strong>
                <span>Reaching {typeDef.name} with your credentials</span>
              </div>
            </>
          ) : testResult?.success ? (
            <>
              <CheckCircle2 size={18} style={{ color: '#10b981' }} />
              <div>
                <strong style={{ color: '#10b981' }}>Connection successful</strong>
                <span>{testResult.message || `Connected to ${typeDef.name}.`}</span>
              </div>
            </>
          ) : testResult ? (
            <>
              <AlertCircle size={18} style={{ color: '#f87171' }} />
              <div>
                <strong style={{ color: '#f87171' }}>Connection failed</strong>
                <span>{testResult.error || 'Check credentials and network reachability.'}</span>
              </div>
            </>
          ) : (
            <>
              <AlertCircle size={18} style={{ color: '#6b7280' }} />
              <div>
                <strong>Not tested yet</strong>
                <span>Fill in credentials above, then test the connection.</span>
              </div>
            </>
          )}
        </div>
        <button
          type="button"
          className="site-setup-btn secondary"
          onClick={runTest}
          disabled={testing}
        >
          {testing ? 'Testing...' : testResult ? 'Retry' : 'Test Connection'}
        </button>
      </div>

      <div className="step-actions">
        <button className="site-setup-btn ghost" onClick={handleSkipThis}>
          <SkipForward size={14} /> Skip this one
        </button>
        <button
          className="site-setup-btn primary"
          onClick={handleSaveAndContinue}
          disabled={!canSave}
        >
          {saving
            ? 'Saving...'
            : idx + 1 < selected.length
              ? 'Save & Next Integration'
              : 'Save & Continue'}
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
};

export default IntegrationConfigStep;
